import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api, endpoints } from '@/lib/api-client';

export interface AdminContext {
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  organizationId?: string;
}

interface AdminContextType {
  adminContext: AdminContext | null;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  refreshAdminContext: () => Promise<void>;
}

const AdminContextReact = createContext<AdminContextType | undefined>(undefined);

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [adminContext, setAdminContext] = useState<AdminContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAdminContext = useCallback(async () => {
    if (!isAuthenticated) {
      setAdminContext(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get<{
        user: { id: string; email: string; name: string };
        adminContext: AdminContext;
      }>(endpoints.adminContext, { skipAuthRedirect: true });

      setAdminContext(response.adminContext);
    } catch (error) {
      // User is not an admin or endpoint failed
      console.error('Failed to fetch admin context:', error);
      setAdminContext(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchAdminContext();
  }, [fetchAdminContext]);

  const refreshAdminContext = useCallback(async () => {
    setIsLoading(true);
    await fetchAdminContext();
  }, [fetchAdminContext]);

  const isAdmin = adminContext?.isSuperAdmin || adminContext?.isOrgAdmin || false;
  const isSuperAdmin = adminContext?.isSuperAdmin || false;
  const isOrgAdmin = adminContext?.isOrgAdmin || false;

  return (
    <AdminContextReact.Provider
      value={{
        adminContext,
        isLoading,
        isAdmin,
        isSuperAdmin,
        isOrgAdmin,
        refreshAdminContext,
      }}
    >
      {children}
    </AdminContextReact.Provider>
  );
}

export function useAdmin(): AdminContextType {
  const context = useContext(AdminContextReact);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

export { AdminContextReact };
